import React, { useState, useEffect, useCallback } from "react";
import {
  CssBaseline,
  ThemeProvider,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import UserInputForm from "./components/user_input/UserInputForm";
import StatusDisplay from "./components/StatusDisplay";
import ResultsTables from "./components/results_reports/ResultsTables";
import UserProfile from "./components/user/UserProfile";
import FeedbackWidget from "./components/user_feedback/FeedbackWidget";
import { auth } from "./config/firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { styles, theme, drawerWidth } from "./Home.style";
import colors from "./constants/colors";
import MenuHidden from "./components/top_bar_menu/menuHidden";
import MenuRow from "./components/top_bar_menu/menuRow";
import { useNavigate } from "react-router-dom";
import ErrorPage from "./components/ErrorPage";
import TutorialContent from "./components/tutorial/TutorialContent";
import {
  getJobHistory,
  createJob,
  getJobResults,
  getJobStatus,
  getUserCredits,
  getUserNotifications,
  markNotificationRead,
  logout,
  cancelJob,
} from "./services/API";

import { formDataModel } from "./constants/formDataModel";
import TopMenuOverlay from "./components/tutorial/TopMenuOverlay";
import { FONT_SIZES } from "./constants/fontSizes";

function Home() {
  const [currentJobId, setCurrentJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [formData, setFormData] = useState(formDataModel);
  const [history, setHistory] = useState([]);
  const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [creditAnchorEl, setCreditAnchorEl] = useState(null);
  const [credits, setCredits] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const hideMenu = useMediaQuery(`(max-width:1500px)`);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const navigate = useNavigate();
  const [serverError, setServerError] = useState(false);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && drawerOpen) {
      setDrawerOpen(false);
    } else if (isRightSwipe && !drawerOpen) {
      setDrawerOpen(true);
    }
  };

  const fetchHistory = useCallback(async () => {
    try {
      const historyData = await getJobHistory();
      setHistory(historyData);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    }
  }, []);

  const handleHistoryClick = (event) => {
    setHistoryAnchorEl(event.currentTarget);
    fetchHistory();
  };

  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };

  const handleHistorySelect = useCallback(async (jobId) => {
    try {
      setCurrentJobId(jobId);
      setError(null);
      const jobData = await getJobResults(jobId);

      if (jobData.user_input) {
        setFormData({
          project_description: jobData.user_input.project_description || "",
          candidate_names: Array.isArray(jobData.user_input.candidate_names)
            ? jobData.user_input.candidate_names
            : [],
          num_names: jobData.user_input.num_names || "",
          industry: jobData.user_input.industry || "",
          job_type: jobData.user_input.job_type || "",
          domain_name_candidates: Array.isArray(
            jobData.user_input.domain_name_candidates
          )
            ? jobData.user_input.domain_name_candidates
            : [],
          num_domain_names: jobData.user_input.num_domain_names || "",
          tlds: Array.isArray(jobData.user_input.tlds)
            ? jobData.user_input.tlds
            : [],
        });
      }

      setResults(jobData);
      // Set status to completed if we have results, otherwise check the job status
      if (jobData && Object.keys(jobData).length > 0 && jobData.user_input) {
        // If we have user_input but no other results, the job was likely cancelled
        // Check if there are actual results beyond user_input
        const hasResults = Object.keys(jobData).some(
          (key) => key !== "user_input" && jobData[key]
        );
        if (hasResults) {
          setStatus("completed");
        } else {
          // Job was cancelled but we have user_input to repopulate the form
          setStatus("cancelled");
        }
      } else {
        const statusData = await getJobStatus(jobId);
        setStatus(statusData.status);
      }
      handleHistoryClose();
    } catch (error) {
      console.error("Error fetching job results:", error);
      // Check if the error is due to job being cancelled
      if (error.message && error.message.includes("Job was cancelled")) {
        setStatus("cancelled");
        setError(null); // Clear any error since this is expected behavior
        setResults(null);
      } else {
        setError("Failed to load job data");
        setStatus("failed");
        setResults(null);
      }
    }
  }, []);

  const fetchResults = useCallback(async () => {
    try {
      const resultsData = await getJobResults(currentJobId);
      setResults(resultsData);
    } catch (err) {
      setError(`Failed to fetch results: ${err.message}`);
    }
  }, [currentJobId]);

  useEffect(() => {
    let pollInterval;

    const pollJobStatus = async () => {
      try {
        const statusData = await getJobStatus(currentJobId);
        const jobStatus = statusData.status;

        // Only update state if status has changed
        if (jobStatus !== status) {
          setStatus(jobStatus);
        }

        if (jobStatus === "completed") {
          clearInterval(pollInterval);
          fetchResults();
          setIsProcessing(false);
        } else if (
          jobStatus === "failed" ||
          jobStatus === "Prompt Insecure" ||
          jobStatus === "cancelled"
        ) {
          clearInterval(pollInterval);
          if (jobStatus === "failed") {
            setError(
              statusData.error || "Job processing failed. Please try again."
            );
          }
          setIsProcessing(false);
          setCurrentJobId(null);
        }
      } catch (err) {
        clearInterval(pollInterval);
        setError(`Failed to poll job status: ${err.message}`);
        setIsProcessing(false);
        setCurrentJobId(null);
      }
    };

    // Only start polling if we have a job ID and we're not in a failed state
    if (
      currentJobId &&
      status !== "failed" &&
      status !== "cancelled" &&
      status !== "Prompt Insecure" &&
      !error
    ) {
      pollInterval = setInterval(pollJobStatus, 2000);
    }

    // Cleanup function
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [currentJobId, status, error]);

  // Separate effect for handling status changes
  useEffect(() => {
    if (status === "failed" || status === "cancelled" || error) {
      setCurrentJobId(null);
      setIsProcessing(false);
    }
  }, [status, error]);

  const combine_name_candidates = (chippedNames, inputBoxNames) => {
    // Handle undefined or null inputBoxNames
    if (!inputBoxNames) return chippedNames || [];

    // Ensure inputBoxNames is a string before calling trim
    const inputBoxNameCollection = String(inputBoxNames).trim().split(",");
    const combinedNames = Array.from(
      new Set([...inputBoxNameCollection, ...(chippedNames || [])])
    );
    return combinedNames.filter(Boolean);
  };

  const handleSubmit = async (formData) => {
    try {
      setDrawerOpen(false);
      setIsProcessing(true);
      setError(null);
      setResults(null);
      setStatus("pending");
      // Get current user and refresh token
      const user = auth.currentUser;
      if (!user) {
        throw new Error("No authenticated user found");
      }

      // Force token refresh
      await user.getIdToken(true);
      const combinedCandidateNames = combine_name_candidates(
        formData.candidate_names,
        formData.candidate_names_input_box
      );
      const combinedDomainNames = combine_name_candidates(
        formData.domain_name_candidates,
        formData.domain_name_candidates_input_box
      );

      const response = await createJob({
        ...formData,
        candidate_names: combinedCandidateNames,
        domain_name_candidates: combinedDomainNames,
      });

      // Only set current job ID and start polling if the job was created successfully
      if (response && response.job_id) {
        setCurrentJobId(response.job_id);
        setFormData((prev) => ({
          ...prev,
          project_description: formData.project_description || "",
          candidate_names: combinedCandidateNames,
          num_names: formData.num_names || "",
          industry: formData.industry || "",
          tlds: formData.tlds || [],
          num_domain_names: formData.num_domain_names || "",
          job_type: formData.job_type || "",
          domain_name_candidates: combinedDomainNames,
        }));
      } else {
        throw new Error("Failed to create job: No job ID received");
      }
    } catch (err) {
      setError(`Failed to submit job: ${err.message}`);
      setIsProcessing(false);
      setStatus("failed"); // Set status to failed to prevent polling
      setCurrentJobId(null); // Clear current job ID to prevent polling
    }
  };

  const handleAddToCandidates = (formDataKey, nameCandidate, action) => {
    if (nameCandidate) {
      setFormData((prev) => ({
        ...prev,
        [formDataKey]:
          action === "add"
            ? Array.from(new Set([...prev[formDataKey], nameCandidate]))
            : prev[formDataKey].filter((n) => n !== nameCandidate),
      }));
      // Then open the drawer
      // setDrawerOpen(true);
    }
  };

  const handleAddAllCandidates = (formDataKey, nameCandidates, action) => {
    nameCandidates.forEach((name) => {
      handleAddToCandidates(formDataKey, name, action);
    });
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    setShowProfile(true);
    handleUserMenuClose();
  };

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      logout(); // Use our enhanced logout function
      navigate("/auth");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationSelect = async (notification) => {
    try {
      if (!notification.read) {
        await markNotificationRead(notification.id);
        // Update local state
        setNotifications((prevNotifications) =>
          prevNotifications.map((n) =>
            n.id === notification.id ? { ...n, read: true } : n
          )
        );
      }
      handleNotificationClose();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleCreditClick = async (event) => {
    setCreditAnchorEl(event.currentTarget);
    try {
      const updatedCredits = await getUserCredits();
      setCredits(updatedCredits);
    } catch (error) {
      console.error("Failed to fetch updated credits:", error);
    }
  };

  const handleCreditClose = () => {
    setCreditAnchorEl(null);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  useEffect(() => {
    // Load Stripe script
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/buy-button.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on component unmount
      document.body.removeChild(script);
    };
  }, []);

  const checkServerConnection = useCallback(async () => {
    try {
      // Try to fetch user credits as a way to check server connection
      await getUserCredits();
      setServerError(false);
    } catch (error) {
      console.error("Server connection error:", error);
      setServerError(true);
    }
  }, []);

  // Add server connection check on component mount
  useEffect(() => {
    checkServerConnection();
  }, [checkServerConnection]);

  // Modify existing fetchCredits to handle server errors
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const userCredits = await getUserCredits();
        setCredits(userCredits);
        setServerError(false);
      } catch (error) {
        console.error("Failed to fetch credits:", error);
        setServerError(true);
      }
    };
    fetchCredits();
  }, [error, fetchResults, status]);

  // Add useEffect to fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const userNotifications = await getUserNotifications();
        // Ensure we always set an array, even if the API returns null/undefined
        setNotifications(
          Array.isArray(userNotifications) ? userNotifications : []
        );
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
        setNotifications([]); // Set empty array on error
      }
    };

    fetchNotifications();
  }, [error, fetchResults, status]);

  // Separate effect for handling job status changes
  useEffect(() => {
    if (
      status === "completed" ||
      status === "failed" ||
      status === "Prompt Insecure" ||
      status === "cancelled"
    ) {
      // Fetch credits and notifications only when job completes, fails, or is insecure
      const fetchUpdates = async () => {
        try {
          const [userCredits, userNotifications] = await Promise.all([
            getUserCredits(),
            getUserNotifications(),
          ]);
          setCredits(userCredits);
          setNotifications(
            Array.isArray(userNotifications) ? userNotifications : []
          );
        } catch (error) {
          console.error("Failed to fetch updates:", error);
        }
      };
      fetchUpdates();
    }
  }, [status]);

  useEffect(() => {
    // Check if user is authenticated
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Skip auth check for password reset route
      if (window.location.pathname === "/reset_password") {
        return;
      }
      if (!user) {
        navigate("/auth");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleCancelJob = async () => {
    if (!currentJobId) return;
    try {
      const result = await cancelJob(currentJobId);
      setStatus(result.status);
      setCurrentJobId(null);
      setIsProcessing(false);
      setError(null); // Clear any error state to ensure polling stops
    } catch (err) {
      setError(`Failed to cancel job: ${err.message}`);
      setIsProcessing(false);
    }
  };

  let coreComponent;

  if (serverError) {
    coreComponent = (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorPage
          message="Unable to connect to the server.
           Either the server is down or you are not connected to the internet."
          onRetry={checkServerConnection}
        />
      </ThemeProvider>
    );
  } else {
    if (results || status) {
      coreComponent = (
        <>
          <Toolbar />
          <Box
            sx={{
              ...styles.resultsContainer,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minHeight: "calc(100vh - 64px)", // Subtract toolbar height
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "1200px", // Match the report width
                mt: results ? 0 : "calc(50vh - 100px)", // Center vertically only when no results
                px: 2, // Add some padding on the sides
              }}
            >
              <StatusDisplay
                status={status}
                error={error}
                setShowTutorial={setShowTutorial}
                onCancelJob={handleCancelJob}
                jobId={currentJobId}
              />
            </Box>
            {results && (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "1200px",
                  px: 0,
                  margin: 0,
                }}
              >
                <ResultsTables
                  results={results}
                  jobType={formData.job_type}
                  handleAddToCandidates={handleAddToCandidates}
                  handleAddAllCandidates={handleAddAllCandidates}
                  formData={formData}
                  setDrawerOpen={setDrawerOpen}
                  setMobileMenuAnchorEl={setMobileMenuAnchorEl}
                />
              </Box>
            )}
          </Box>
        </>
      );
    } else {
      coreComponent = (
        <Box
          sx={{
            ...styles.tutorialContainer,
          }}
        >
          <TutorialContent />
        </Box>
      );
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={styles.appContainer}>
        <AppBar position="fixed" sx={styles.appBar(drawerOpen, drawerWidth)}>
          <Toolbar sx={styles.toolbar}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setDrawerOpen(!drawerOpen)}
              edge="start"
            >
              {drawerOpen ? (
                <ChevronLeftIcon sx={styles.menuIcon} />
              ) : (
                <MenuIcon sx={styles.menuIcon} />
              )}
            </IconButton>
            <Box
              sx={{
                display: "flex",
                pl: { xs: 1, sm: 5 },
                alignItems: "baseline",
                justifyContent: "center",
                gap: { xs: 0, sm: 5 },
                flexDirection: { xs: "column", sm: "row" },
                width: "90%",
              }}
            >
              <Typography
                variant="h5"
                noWrap
                component="div"
                sx={{
                  ...styles.title,
                  color: colors.blue_green[900],
                  fontSize: FONT_SIZES.SUBTITLE,
                }}
              >
                Name Pilot
              </Typography>
              <Typography
                variant="h5"
                noWrap
                component="div"
                sx={{
                  ...styles.title,
                  color: colors.blue_green[900],
                  fontSize: {
                    xs: FONT_SIZES.PARAGRAPH.xs,
                    sm: FONT_SIZES.SUBTITLE.sm,
                  },
                  maxWidth: { xs: "300px", sm: "80%", md: "100%" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Brand Generation and Analysis
              </Typography>
            </Box>
            {hideMenu ? (
              <MenuHidden
                handleMobileMenuOpen={handleMobileMenuOpen}
                handleMobileMenuClose={handleMobileMenuClose}
                mobileMenuAnchorEl={mobileMenuAnchorEl}
                handleCreditClick={handleCreditClick}
                handleNotificationClick={handleNotificationClick}
                handleHistoryClick={handleHistoryClick}
                handleUserMenuOpen={handleUserMenuOpen}
                handleUserMenuClose={handleUserMenuClose}
                credits={credits}
                notifications={notifications}
                history={history}
                handleHistorySelect={handleHistorySelect}
                setShowTutorial={setShowTutorial}
                handleCreditClose={handleCreditClose}
                creditAnchorEl={creditAnchorEl}
                notificationAnchorEl={notificationAnchorEl}
                handleNotificationClose={handleNotificationClose}
                handleNotificationSelect={handleNotificationSelect}
                handleProfileClick={handleProfileClick}
                handleLogout={handleLogout}
                userMenuAnchor={userMenuAnchor}
                historyAnchorEl={historyAnchorEl}
                handleHistoryClose={handleHistoryClose}
              />
            ) : (
              <MenuRow
                setShowTutorial={setShowTutorial}
                handleCreditClick={handleCreditClick}
                credits={credits}
                creditAnchorEl={creditAnchorEl}
                handleCreditClose={handleCreditClose}
                handleNotificationClick={handleNotificationClick}
                notifications={notifications}
                notificationAnchorEl={notificationAnchorEl}
                handleNotificationClose={handleNotificationClose}
                handleNotificationSelect={handleNotificationSelect}
                handleHistoryClick={handleHistoryClick}
                historyAnchorEl={historyAnchorEl}
                handleHistoryClose={handleHistoryClose}
                history={history}
                handleHistorySelect={handleHistorySelect}
                handleUserMenuOpen={handleUserMenuOpen}
                handleUserMenuClose={handleUserMenuClose}
                userMenuAnchor={userMenuAnchor}
                handleProfileClick={handleProfileClick}
                handleLogout={handleLogout}
              />
            )}
          </Toolbar>
        </AppBar>
        <Drawer
          sx={styles.drawer(drawerOpen, drawerWidth)}
          variant={isMobile ? "temporary" : "persistent"}
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          ModalProps={{
            keepMounted: true,
            onClose: () => setDrawerOpen(false),
          }}
        >
          <Toolbar sx={styles.drawerHeader}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ ...styles.drawerHeaderText, fontSize: FONT_SIZES.SUBTITLE }}
              onClick={() => setDrawerOpen(!drawerOpen)}
            >
              Describe Your Brand
            </Typography>
          </Toolbar>
          <Box
            sx={{
              ...styles.drawerContent,
              ml: { xs: 0, sm: 2 },
              mr: { xs: 0, sm: 2 },
            }}
          >
            <UserInputForm
              onSubmit={handleSubmit}
              disabled={isProcessing}
              formData={formData}
              setFormData={setFormData}
              setResults={setResults}
              setStatus={setStatus}
            />
          </Box>
        </Drawer>
        {showProfile && (
          <TopMenuOverlay
            child={<UserProfile />}
            onClose={handleCloseProfile}
          />
        )}
        <Box sx={styles.mainContent}>
          <Box component="main" sx={{ ...styles.mainBox }}>
            {coreComponent}
          </Box>
        </Box>
        <FeedbackWidget />
        {showTutorial && (
          <TopMenuOverlay
            child={<TutorialContent />}
            onClose={() => setShowTutorial(false)}
          />
        )}
      </Box>
    </ThemeProvider>
  );
}

export default Home;
